import { useMemo } from 'react';

import { isFinanceProject, isGlobalProject } from 'config';
import { renderCopyHover, renderTextLineClamp } from 'helpers';
import { TFunc, useTranslation } from 'libs/i18n';
import { TableColumn } from 'libs/ui';

import { ClientsListItem } from '../../types';

import { renderName, renderStatus } from './renders';

export const getColumns = (t: TFunc): TableColumn<ClientsListItem>[] => [
  {
    field: 'id',
    title: t('client.clients.personal.columns.id'),
    minWidth: 96,
    flex: 1,
    renderCell: (item) => renderCopyHover(item.id),
  },

  {
    field: 'lastSessionDate',
    title: t('client.clients.personal.columns.lastSessionDate'),
    minWidth: 145,
    flex: 1,
    renderCell: (item) => renderCopyHover(item.lastSessionDate),
  },

  {
    field: 'lastName',
    title: t('client.clients.personal.columns.fullName'),
    sortable: true,
    minWidth: 204,
    flex: 1,
    renderCell: (item) => renderName(item),
  },

  {
    field: 'personalAccountNumber',
    title: t('client.clients.personal.columns.personalAccountNumber'),
    minWidth: 200,
    flex: 1,
    renderCell: (item) => renderTextLineClamp(item.personalAccountNumber),
  },

  {
    field: 'personalAccountOpeningDate',
    title: t('client.clients.personal.columns.personalAccountOpeningDate'),
    minWidth: 145,
    flex: 1,
    renderCell: (item) => renderCopyHover(item.personalAccountOpeningDate),
  },

  {
    field: 'code',
    title: t('client.clients.personal.columns.code'),
    minWidth: 126,
    flex: 1,
    renderCell: (item) => renderTextLineClamp(item.code),
  },
  {
    field: 'centralDepositoryAccountNumber',
    title: t('client.clients.personal.columns.centralDepositoryAccountNumber'),
    minWidth: 120,
    flex: 1,
    renderCell: (item) =>
      renderTextLineClamp(item.centralDepositoryAccountNumber),
  },
  {
    field: 'email',
    title: t('client.clients.personal.columns.email'),
    minWidth: 183,
    flex: 1,
    renderCell: (item) => renderTextLineClamp(item.email),
  },
  {
    field: 'phone',
    title: t('client.clients.personal.columns.phone'),
    minWidth: 185,
    renderCell: (item) => renderTextLineClamp(item.phone),
  },
  {
    field: 'countryRegistration',
    title: t('client.clients.personal.columns.countryRegistration'),
    minWidth: 160,
    flex: 1,
    renderCell: (item) => renderTextLineClamp(item.countryRegistration),
  },
  {
    field: 'citizenship',
    title: t('client.clients.personal.columns.citizenship'),
    minWidth: 160,
    flex: 1,
    renderCell: (item) => renderTextLineClamp(item.citizenship?.name),
  },
  {
    field: 'location',
    title: t('client.clients.personal.columns.location'),
    minWidth: 148,
    flex: 1,
    renderCell: (item) => renderTextLineClamp(item.location),
  },
  {
    field: 'onboardingStatus',
    title: t('client.clients.personal.columns.status'),
    sortable: true,
    minWidth: 346,
    flex: 1,
    renderCell: (item) => renderStatus(item, t),
  },
];

export const useColumns = () => {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const columns = getColumns(t);

    return columns.filter(({ field }) => {
      if (isFinanceProject()) {
        // Exclude citizenship for finance project
        return field !== 'citizenship';
      }

      if (isGlobalProject()) {
        // Exclude countryRegistration for global project
        return field !== 'countryRegistration';
      }

      return true;
    });
  }, [t]);

  return columns;
};
